import { useEffect, useState, type ReactNode } from 'react';
import QRCode from 'qrcode';

import { Modal, Notice } from '@/components/Modal';

/**
 * 링크 하나를 QR 로 그려 주는 창 — <b>벽에 붙일 종이 한 장</b>을 위한 것이다.
 *
 * <b>이미지를 서버에 두지 않는다.</b> QR 은 링크를 그대로 그린 것이라 링크만 있으면 언제든 다시
 * 그릴 수 있고, 저장해 두면 링크를 고쳤을 때 낡은 QR 이 남는다 — 그 종이는 벽에 붙어 있다.
 *
 * 그리기는 브라우저에서 끝난다. 외부 QR 생성 API 를 쓰지 않는 이유는 그 순간 링크가 제3자
 * 서버로 나가기 때문이다.
 *
 * <p>쓰는 곳이 둘이다(2026-09-17) — 현장 오픈채팅 초대와 구내식당 점심 체크. 그래서 pmis 폴더에서
 * 여기로 옮겨 왔고, 아래 {@code hint} 는 <b>찍으면 무엇이 열리는가</b>를 부르는 쪽이 적는 자리다.
 */
export function QrCodeModal({
  title,
  url,
  hint,
  onClose,
}: {
  title: string;
  url: string;
  hint?: ReactNode;
  onClose: () => void;
}) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(url, { width: 512, margin: 1, errorCorrectionLevel: 'M' })
      .then((value) => {
        if (alive) setDataUrl(value);
      })
      .catch(() => {
        if (alive) setError('QR 을 그리지 못했습니다.');
      });
    return () => {
      alive = false;
    };
  }, [url]);

  return (
    <Modal
      title={`${title} · QR`}
      onClose={onClose}
      maxWidth="360px"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            닫기
          </button>
          <button className="btn-primary" onClick={() => window.print()}>
            인쇄
          </button>
        </>
      }
    >
      <div className="space-y-3 text-center">
        {error && <Notice tone="error">{error}</Notice>}
        {dataUrl && (
          // 인쇄하면 이 이미지만 남는다(index.css 의 .print-only 규칙과 같은 자리).
          <img
            src={dataUrl}
            alt={`${title} QR`}
            style={{ width: '240px', height: '240px', margin: '0 auto', imageRendering: 'pixelated' }}
          />
        )}
        <p style={{ fontSize: '11px', color: 'var(--text-muted)', wordBreak: 'break-all' }}>{url}</p>
        {hint && <p style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{hint}</p>}
      </div>
    </Modal>
  );
}
